import React, { useEffect, useRef, useState } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { hooks, constants as c } from '@lifesize/clients.sdk';
import { CallLayout, CompositedLayout, AudioStream } from '@lifesize/clients.sdk/dist/components';
import CallMessaging from 'components/InCall/CallMessaging';
import Header from 'containers/InCall/InCallHeader/InCallHeaderContainer';
import Footer from 'containers/InCall/InCallFooter/InCallFooterContainer';
import useViewport from 'hooks/useViewport';
import { SelfViewState } from 'reducers/selfViewSlice';
import { RootState } from 'store/reducers';
import { setAudioOutput } from 'utils/deviceUtils';
import { getMaximumReceiveStreams } from 'utils/mediaConstraints';
import { isMobile } from 'utils/browser-utils';
import classes from './CallViewHolder.module.scss';
import useIsMinimized from 'hooks/useIsMinimized';
import clsx from 'clsx';
import TurtleContainer from 'containers/InCall/Turtle/TurtleContainer';
import { handleResizeContainer } from 'actions/InCall/InCallVideo';
import { toggleMinimized } from 'actions/InCall/InCallVideo';

/**
 * Top level call holder view. Simply contains the Header, Video, and Footer elements.
 */

const messages = defineMessages({
  popoutLabel: { id: 'components.incall.controls.popout', defaultMessage: 'Pop-Out Content' }
});

interface Props {
  intl: { formatMessage: Function };
  webappCloseWhiteboard: () => null;
}

export const CallViewHolder = (props: Props) => {
  const { webappCloseWhiteboard } = props;
  const callViewHolder = useRef(null);
  const call = hooks.useCallState();
  const callDisconnected = call && call.state === c.CALL_STATE_DISCONNECTED;
  const audioOnlyStreams = hooks.useRemoteStreamsAudioOnly();
  const remoteStreams = hooks.useRemoteStreams();
  // TODO: PT: this should be moved into the call layout component. TODO before SFU multistream GA release.
  const { sfuSettings } = hooks.useAccountSettings();
  const isSfuMultiStream = getMaximumReceiveStreams() > 1 && !(sfuSettings?.sfuMultiStream === false);
  const isMinimized = useIsMinimized();
  const isMultiStreamLayout = isSfuMultiStream && !isMobile();
  const LayoutComponent = isMultiStreamLayout ? CallLayout : CompositedLayout;
  const { isFooterCompact, isMobileView } = useViewport();
  const { layout, presentationLayout } = hooks.useMedia();
  const remotePresentationStream = hooks.useRemotePresentationStream();
  const selfView: SelfViewState = useSelector((state: RootState) => state.app.selfView);
  const showMobileView = isMobileView || isMobile(); // for parity with when we hide the layout switch in the footer
  const showPipInLayout = selfView.on && isFooterCompact && layout !== c.SFU_LAYOUT_GALLERY;
  const { isWebConferencing, speakers } = useSelector((state: any) => {
    const isWebConferencing = state.app.call?.isWebConference;
    const speakers = state.app.mediaSettings?.speakers;

    return {
      isWebConferencing,
      speakers
    };
  });
  const inlinePresentation = !!remotePresentationStream && presentationLayout !== c.SFU_PRES_LAYOUT_POPPEDOUT;
  const dispatch = useDispatch();
  const videoLayout = useSelector((state: RootState) => state?.app?.inCallVideo?.videoLayout);
  const [isTurtleVisible, toggleTurtle] = useState(false);

  useEffect(() => {
    window.addEventListener('beforeunload', webappCloseWhiteboard);
    return () => {
      window.removeEventListener('beforeunload', webappCloseWhiteboard);
    };
  }, [webappCloseWhiteboard]);

  useEffect(() => {
    if (callDisconnected) webappCloseWhiteboard();
  }, [callDisconnected, webappCloseWhiteboard]);

  useEffect(() => {
    if (speakers && remoteStreams.length) {
      setAudioOutput(speakers);
    }
  }, [remoteStreams.length, speakers]);

  useEffect(() => {
    let observer;
    const callViewHolderElt = callViewHolder.current;

    // set viewport width for layouts
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    const setVw = () => {
      // @ts-ignore
      const vw = (callViewHolderElt ? callViewHolderElt.offsetWidth : window.innerWidth) * 0.01;
      // @ts-ignore
      dispatch(handleResizeContainer(callViewHolderElt?.getBoundingClientRect()));
      document.documentElement.style.setProperty('--vw', `${vw}px`);
    };

    if (callViewHolderElt) {
      observer = new window.BackupResizeObserver(setVw);
      observer.observe(callViewHolderElt);
    }
    setVw();

    return () => {
      // close the whiteboard if the call ends
      if (callViewHolderElt) {
        observer.unobserve(callViewHolderElt);
      }
      observer = null;
    };
  }, [callViewHolder, dispatch, inlinePresentation, isSfuMultiStream]);

  useEffect(() => {
    if (callDisconnected && isMinimized) dispatch(toggleMinimized());
  }, [isMinimized, callDisconnected, dispatch]);

  return (
    <div className={classes.container}>
      <Header isMultiStreamLayout={isMultiStreamLayout} />
      <div
        id="callLayout-holder-div"
        className={clsx(classes.layoutContainer, isMinimized && classes.minimizedLayoutContainer)}
        ref={callViewHolder}
        onMouseOver={() => toggleTurtle(true)}
        onMouseLeave={() => toggleTurtle(false)}
      >
        <LayoutComponent
          muteRemotes={isWebConferencing}
          intl={{ popoutLabel: props.intl.formatMessage(messages.popoutLabel) }}
          isMobileView={showMobileView}
          showMetadata={isSfuMultiStream}
          showPip={showPipInLayout}
          isMinimized={isMinimized}
          // @ts-ignore
          Slider={<TurtleContainer isTurtleVisible={isTurtleVisible} />}
          videoLayout={videoLayout?.videos}
        />
      </div>
      {audioOnlyStreams.map((s, i) => (
        <AudioStream key={`audio-only-${i}`} mediaStream={s.stream} />
      ))}
      <CallMessaging />
      <Footer />
    </div>
  );
};

export default injectIntl(CallViewHolder);
